import {Box, HBox} from '@buncha/components/core/view';
import {H3Text, H5Text, H6Text} from '@buncha/components/core/text';
import {
  OrderData,
  OrderHistoryStatus,
  OrderInfoParams,
} from '@buncha/B2B/hooks/type';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import CancelOrderHeader from '@buncha/B2B/components/cancelOrderHeader/CancelOrderHeader';
import {DeliveryLocationStatus} from '@buncha/batchDetails/types';
import {Divider} from 'native-base';
import EditIcon from '@buncha/components/core/icon/EditIcon';
import {EventType} from '@buncha/config/analytics/type';
import Faqs from '@buncha/B2B/components/Faqs';
import Header from '@buncha/components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import {ImageBackground} from 'react-native';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import NeedHelpB2B from '@buncha/B2B/components/needHelpB2B/NeedHelpB2B';
import NoDataFound from '@buncha/B2B/components/noDataFound/NoDataFound';
import OrderHeader from '@buncha/B2B/components/orderHeader/OrderHeader';
import OrderInfoSkeleton from '@buncha/B2B/components/orderInfoSkeleton/OrderInfoSkeleton';
import OrderProgressStatus from '@buncha/B2B/components/orderProgressStatus/OrderProgressStatus';
// import PreferredShopper from '@buncha/B2B/components/PreferredShopper';
// import RescheduleOrderHeader from '@buncha/B2B/components/rescheduleOrderHeader/RescheduleOrderHeader';
import Scrollview from '@buncha/components/composites/scrollview';
import TipComponent from '@buncha/B2B/components/tipComponent/TipComponent';
import {Touchable} from '@buncha/components/core/touchable';
import UpdateDeliveryBottomsheet from '@buncha/B2B/components/updateDeliveryBottomsheet';
import {decryptFromBase64} from '@buncha/B2B/utils';
import font from '@buncha/appStyles/style/font';
import {navigation} from '@buncha/config/navigation';
import {styles} from './styles';
import {useFetchGetData} from '@buncha/config/hooks/axios';
import {useGetLocale} from '@buncha/localization/hooks';
import {useSocket} from '@buncha/config/socket/hooks';

const emptyArray: string[] = [];

const finishedStatuses = [
  DeliveryLocationStatus.delivered,
  DeliveryLocationStatus.deliveryFailed,
  DeliveryLocationStatus.cancelled,
  DeliveryLocationStatus.receiptGenerated,
  DeliveryLocationStatus.refunded,
];
const OrderInfo: React.FC = () => {
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const route = useRoute();
  const params: OrderInfoParams = route?.params;
  const [stateIndex, setStateIndex] = useState(1);
  const orderId = params?.orderId?.length
    ? decryptFromBase64(params?.orderId ?? '0')
    : undefined;

  const [getOrderData, orderDataInit, orderLoading, error] = useFetchGetData<{
    data: OrderData;
  }>();

  const [
    getOrderDataForSocket,
    orderDataSocket,
    orderDataSocketLoading,
    errorSocket,
  ] = useFetchGetData<{
    data: OrderData;
  }>();
  const orderData = useMemo(() => {
    return orderDataSocket?.data ? orderDataSocket : orderDataInit;
  }, [orderDataInit, orderDataSocket]);
  const {title, body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.OrderInfo) ?? {};
  const order = orderData?.data?.order;
  // const contactDetails = orderData?.data?.contactDetails;
  const deliveryAddress = orderData?.data?.deliveryAddress;
  const orderStatus = order?.status;
  // const commonShopperDetails =
  //   orderData?.data?.shopperDetails?.id === orderData?.data?.driverDetails?.id
  //     ? orderData?.data?.shopperDetails
  //     : undefined;
  // const showPreferredShopper =
  //   commonShopperDetails?.id &&
  //   !orderData?.data?.preferredShopperId &&
  //   orderStatus === DeliveryLocationStatus.delivered;
  const assigneeDetails = orderData?.data?.assigneeDetails;
  const orderHistory = orderData?.data.orderHistory || [];
  const isClaimed = orderHistory.find(
    item => item.status === OrderHistoryStatus.shoppingInProgress,
  );
  const isCancelled = orderStatus === DeliveryLocationStatus.cancelled;
  const isReschedule = order?.extraInfo?.reschedule?.oldRunId;
  const showTip =
    orderStatus === DeliveryLocationStatus.notStarted &&
    !isClaimed &&
    !isCancelled;
  // const deliveryOrderHistory = orderHistory.find(
  //   item => item.status === OrderHistoryStatus.delivered,
  // );
  const [screenTitle, setScreenTitle] = useState<string>(title?.orderPlace);
  const condition = Boolean(order?.id);
  const socket = useSocket(condition);
  const runId = orderData?.data?.run?.id;
  const orderLegacyId = order?.legacyId;
  const isFocused = useIsFocused();

  const showNotesUpdate =
    orderStatus && !finishedStatuses.includes(orderStatus);

  const [updateDeliveryNotesBottomsheet, setupdateDeliveryNotesBottomsheet] =
    useState(false);

  const toggleUpdateDeliveryNotesBottomsheet = useCallback(() => {
    setupdateDeliveryNotesBottomsheet(prev => !prev);
  }, []);

  const renderDetails = useCallback(
    (
      key: string,
      value: string,
      onEditPress?: () => void,
      onHeaderPress?: () => void,
      maxLines?: number,
    ) => {
      return (
        <Box style={styles.renderDetailsContainer} key={key}>
          <HBox>
            <H3Text
              style={[
                styles.customerAndDeliveryHeader,
                {color: theme?.text.blue[300]},
              ]}>
              {key}
            </H3Text>
            <If condition={onEditPress}>
              <Touchable onPress={onEditPress}>
                <EditIcon viewBox="0 0 15 15" width={22} height={22} />
              </Touchable>
            </If>
          </HBox>
          <Touchable onPress={onHeaderPress}>
            <H5Text
              numberOfLines={maxLines}
              style={[
                styles.renderDetailsValueText,
                {
                  color: theme?.text.blue[300],
                  fontFamily: muliFont[400].normal,
                },
              ]}>
              {value}
            </H5Text>
          </Touchable>
        </Box>
      );
    },
    [muliFont, theme?.text.blue],
  );

  const getHeader = useMemo(() => {
    if (isCancelled) return <CancelOrderHeader />;
    // if (isReschedule)
    //   return <RescheduleOrderHeader run={orderData?.data.run} />;
    return <OrderHeader />;
  }, [isCancelled]);

  useEffect(() => {
    if (orderId?.length && isFocused)
      getOrderData(`/api/v1/customer/order/${orderId}/info`, {});
    else if (!orderId?.length)
      navigation.navigate(MainScreens.B2B, {
        screen: B2BUserScreens.UserLogin,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, isFocused]);

  const updateData = useCallback(
    async () =>
      await getOrderDataForSocket(`/api/v1/customer/order/${orderId}/info`, {}),
    [getOrderDataForSocket, orderId],
  );

  const socketUpdateData = useCallback(async () => {
    await getOrderDataForSocket(`/api/v1/customer/order/${orderId}/info`, {});
    setStateIndex(prev => prev + 1);
  }, [getOrderDataForSocket, orderId]);

  useEffect(() => {
    if (runId && orderLegacyId && orderId && socket && isFocused)
      socket.on(
        `order_view_status_updated_${runId}_${orderLegacyId}`,
        async () => socketUpdateData(),
      );
    else
      socket.off(
        `order_view_status_updated_${runId}_${orderLegacyId}`,
        async () => socketUpdateData(),
      );

    return () => {
      socket.off(
        `order_view_status_updated_${runId}_${orderLegacyId}`,
        async () =>
          getOrderDataForSocket(`/api/v1/customer/order/${orderId}/info`, {}),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderLegacyId, runId, socket, isFocused]);

  const setAuthParams = useCallback((contactId: string, orderUUID: string) => {
    const authParams = {contactId, orderId: orderUUID};
    sessionStorage.setItem('authParams', JSON.stringify(authParams));
  }, []);

  useEffect(() => {
    if (
      orderDataInit?.data?.contactDetails?.id &&
      orderDataInit?.data?.order?.id
    )
      setAuthParams(
        orderDataInit?.data?.contactDetails?.id,
        orderDataInit?.data?.order?.id,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDataInit?.data?.contactDetails?.id, orderDataInit?.data?.order?.id]);

  useEffect(() => {
    if (isCancelled) setScreenTitle(title?.orderCancelled || '');
  }, [isCancelled, title?.orderCancelled]);

  useEffect(() => {
    if (orderData?.data?.order?.id) {
      Analytics.setIdentifiedUserAttribute(orderData?.data?.contactDetails);
      Analytics.eventWithProps('Order View page', {
        type: EventType.page,
        id: orderData?.data.contactDetails.id,
        name: `${orderData?.data.contactDetails.firstName} ${orderData?.data.contactDetails.lastName}`,
        runId,
        orderId: order?.legacyId,
        externalOrderId: order?.externalOrderId,
        source: params?.source,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData?.data?.order?.id]);

  if (orderLoading) return <OrderInfoSkeleton />;

  if (error?.message || errorSocket?.message) return <NoDataFound />;

  return (
    <Header showDefaultLeftHeader={false} hideBottomSafeArea hideUploadFile>
      {getHeader}
      <ImageBackground
        imageStyle={styles.imageStyle}
        style={styles.backgroundStyles}
        resizeMode="stretch"
        source={require('@buncha/resource/images/FruitBG.png')}>
        <Scrollview style={styles.container}>
          <Box style={styles.containerPadding}>
            <If condition={isReschedule || isCancelled}>
              <Box style={styles.supportStyle}>
                <NeedHelpB2B
                  source="Order View"
                  runId={runId || 0}
                  legacyId={order?.legacyId || 0}
                />
              </Box>
            </If>
            <If condition={showTip}>
              <TipComponent />
            </If>
            <H6Text
              style={[
                styles.orderNumberText,
                {
                  fontFamily: isCancelled
                    ? muliFont[600].normal
                    : muliFont[400].normal,
                  fontSize: isCancelled
                    ? font.FontSize.fontH5.fontSize
                    : undefined,
                  color: isCancelled ? theme?.text.blue[300] : undefined,
                },
              ]}>
              {title?.OrderNumber}
              {order?.externalOrderId}
            </H6Text>
            <If condition={!isCancelled}>
              <H3Text
                style={[
                  styles.orderPlacedText,
                  {color: theme?.text.blue[300]},
                ]}>
                {screenTitle}
              </H3Text>
            </If>
            <OrderProgressStatus
              orderStatus={orderStatus}
              orderHistory={orderHistory}
              routeOrderId={orderId ?? ''}
              deliveryImages={
                orderData?.data?.deliveryInfo?.deliveryImages ?? emptyArray
              }
              legacyId={order?.legacyId ?? 0}
              assigneeDetails={assigneeDetails}
              externalOrderId={order?.externalOrderId}
              run={orderData?.data?.run}
              setScreenTitle={setScreenTitle}
              socketLoading={orderDataSocketLoading}
              stateIndex={stateIndex}
              vendorDelivery={orderData?.data?.vendorDelivery}
              deliveryNotes={order?.deliveryNotes}
              driverDetails={orderData?.data?.driverDetails}
            />
            {/* <If condition={showPreferredShopper}>
              <Divider style={styles.dividerMargin} />
              <PreferredShopper
                preferredShopperDetails={commonShopperDetails}
                orderId={order?.externalOrderId}
              />
            </If> */}
            <Divider style={styles.dividerMargin} />
            {renderDetails(
              body?.deliveryAddress,
              `${deliveryAddress?.line1} ${
                deliveryAddress?.line2?.length ? deliveryAddress?.line2 : ''
              } ${deliveryAddress?.city}, ${deliveryAddress?.zip}`,
            )}
            <Divider style={styles.dividerMargin} />
            {renderDetails(
              showNotesUpdate && !order?.deliveryNotes
                ? body?.addDeliveryNotes
                : body?.deliveryNotes,
              order?.deliveryNotes ?? '',
              showNotesUpdate
                ? toggleUpdateDeliveryNotesBottomsheet
                : undefined,
              showNotesUpdate
                ? undefined
                : toggleUpdateDeliveryNotesBottomsheet,
              2,
            )}
            <Faqs />
          </Box>
        </Scrollview>
      </ImageBackground>
      <UpdateDeliveryBottomsheet
        open={updateDeliveryNotesBottomsheet}
        onClose={toggleUpdateDeliveryNotesBottomsheet}
        notes={order?.deliveryNotes || ''}
        orderId={order?.externalOrderId}
        updateData={updateData}
        showNotesUpdate={showNotesUpdate}
      />
    </Header>
  );
};

export default React.memo(OrderInfo);
